import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, KeyRound, Lock, ArrowRight } from "lucide-react";

export default function AdminEntry() {
  const { setSession } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("creds"); // creds | mfa
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", {
        email,
        password,
        mfa_code: mfaCode || undefined,
        is_admin_portal: true,
      });
      if (data.mfa_required) {
        setStep("mfa");
        setLoading(false);
        return;
      }
      setSession(data.token, data.user);
      nav("/admin");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-ec-ink text-white relative overflow-hidden" data-testid="admin-entry-page">
      {/* Decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -left-40 -top-40 w-[40rem] h-[40rem] border border-white/5 rounded-full" />
        <div className="absolute -right-40 -bottom-40 w-[36rem] h-[36rem] border border-ec-emerald/10 rounded-full" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(34,197,94,0.06),transparent_50%)]" />
      </div>

      <div className="relative z-10 m-auto w-full max-w-md px-6">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-9 h-9 bg-ec-emerald rounded-sm flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-ec-ink" />
          </div>
          <div>
            <div className="eyebrow text-ec-emerald">Master Control</div>
            <div className="font-display font-black text-xl tracking-tight">EthicCred · Admin Portal</div>
          </div>
        </div>

        <div className="bg-black/30 backdrop-blur-xl border border-white/10 rounded-sm p-8">
          {step === "creds" && (
            <>
              <h1 className="font-display text-3xl font-black">Sign in</h1>
              <p className="text-sm text-zinc-400 mt-2">
                This portal is restricted to authorised personnel. All access is logged.
              </p>
              <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-entry-form">
                <div>
                  <Label className="eyebrow text-zinc-400">Email</Label>
                  <Input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 h-11 rounded-sm bg-white/5 border-white/10 text-white focus-visible:ring-ec-emerald"
                    placeholder="admin@example.com"
                    data-testid="admin-entry-email"
                  />
                </div>
                <div>
                  <Label className="eyebrow text-zinc-400">Password</Label>
                  <Input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-2 h-11 rounded-sm bg-white/5 border-white/10 text-white focus-visible:ring-ec-emerald"
                    placeholder="••••••••"
                    data-testid="admin-entry-password"
                  />
                </div>
                {err && <div className="text-sm text-ec-error bg-ec-error/10 border border-ec-error/30 px-3 py-2 rounded-sm" data-testid="admin-entry-error">{err}</div>}
                <Button type="submit" disabled={loading} className="w-full h-11 rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink font-bold gap-2" data-testid="admin-entry-submit">
                  <Lock className="w-4 h-4" />
                  {loading ? "Verifying…" : "Sign in"}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </form>
            </>
          )}

          {step === "mfa" && (
            <>
              <h1 className="font-display text-3xl font-black">Two-factor verification</h1>
              <p className="text-sm text-zinc-400 mt-2">
                Enter the 6-digit code from your authenticator app.
              </p>
              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <Label className="eyebrow text-zinc-400">Authenticator Code</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    required
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/[^0-9]/g, ""))}
                    className="mt-2 h-12 rounded-sm bg-white/5 border-white/10 text-white text-center text-2xl tracking-[0.5em] font-mono-plex focus-visible:ring-ec-emerald"
                    placeholder="000000"
                    data-testid="admin-entry-mfa-code"
                    autoFocus
                  />
                </div>
                {err && <div className="text-sm text-ec-error bg-ec-error/10 border border-ec-error/30 px-3 py-2 rounded-sm" data-testid="admin-entry-mfa-error">{err}</div>}
                <Button type="submit" disabled={loading || mfaCode.length !== 6} className="w-full h-11 rounded-sm bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink font-bold gap-2" data-testid="admin-entry-mfa-submit">
                  <KeyRound className="w-4 h-4" />
                  {loading ? "Verifying…" : "Verify & sign in"}
                </Button>
                <button type="button" onClick={() => { setStep("creds"); setMfaCode(""); setErr(""); }} className="text-xs text-zinc-500 hover:text-white">
                  ← Back to password
                </button>
              </form>
            </>
          )}
        </div>

        <div className="mt-6 text-center text-xs font-mono-plex text-zinc-600">
          Every authentication attempt is logged. Unauthorized access is prosecuted.
        </div>
      </div>
    </div>
  );
}
